import appManager from '@app/appManager';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import AppModule from '@app/lib/AppModule';
import IAppEvents from '@app/lib/IAppEvents';
import IAppLogger from '@app/lib/IAppLogger';
import environment from '@lib/environment';
import { AuthEvents, AuthUser } from '@modules/AuthModule/types';
import axios from 'axios';
import {
  ArbitraryData,
  ConnectionService,
  Log,
  LogSender,
  LogStorage,
} from '@servicetitan/log-service';
import { ContextualLog } from './lib/ContextualLog';

export class KibanaModule extends AppModule {
  private readonly _logService: Log;
  private readonly _logSender: LogSender;

  private _userId: string | null = null;
  private _accountId: string | null = null;
  private _contactCenterId: string | null = null;
  private _orgId: string | null = null;

  constructor(events: IAppEvents, logger: IAppLogger) {
    super(events, logger);

    const logConfig = {
      endpoint: `${environment.API_URL}/v1/log`,
      storageKey: 'host.log.store', // localStorage key for storing logs
    };

    // Configure axios interceptor to send the BFF session cookie on log requests
    this.setupAxiosInterceptor(logConfig.endpoint);

    // initialize or log service
    const connectionService = new ConnectionService();
    const logStorage = new LogStorage(logConfig);
    this._logService = new ContextualLog(logStorage, () =>
      this.getUserContextData(),
    );
    this._logSender = new LogSender(
      connectionService,
      logStorage,
      logConfig,
      this._logService,
    );
  }

  /**
   * Sets up an axios interceptor to send the BFF session cookie
   * specifically for log endpoint requests.
   */
  private setupAxiosInterceptor(logEndpoint: string): void {
    // Skip interceptor setup during test environment to avoid conflicts
    if (import.meta.env.MODE === 'test') {
      return;
    }

    try {
      axios.interceptors.request.use((config) => {
        if (config.url?.includes(logEndpoint)) {
          config.withCredentials = true;
        }
        return config;
      });
    } catch (error) {
      // Silently fail if interceptor setup fails during HMR
      this.app.logger.warn(
        'kibana',
        'Failed to setup axios interceptor for Kibana logs',
        error,
      );
    }
  }

  override preload(): void {
    // register a listener for the auth.signin event so we can
    // get the user context values
    this.app.events.on(AuthEvents.SIGNIN, (user: AuthUser) => {
      this._userId = user.id;
      this._accountId = user.accountId;
      this._contactCenterId = user.contactCenterId;
      this._orgId = user.org.id;
    });

    // register a listener for the auth.signout event so we can
    // clear the user context values
    this.app.events.on(AuthEvents.SIGNOUT, () => {
      this._userId = null;
      this._accountId = null;
      this._contactCenterId = null;
      this._orgId = null;
    });

    // register a listener for all log events so we can send them to
    // the log service
    this.app.logger.onAny((level, key, message, args, error) => {
      // only push to log service if feature flag is enabled
      if (
        !FeatureFlagModule.isEnabled(
          FeatureFlagKeys.featurePushConsoleLogsToKibana,
        )
      ) {
        return;
      }

      // Filter debug logs unless enable-debug-logs-to-kibana flag is enabled
      if (
        level === 'debug' &&
        !FeatureFlagModule.isEnabled(FeatureFlagKeys.enableDebugLogsToKibana)
      ) {
        return;
      }

      // Filter high-frequency status events and JsSIP debug output
      if (
        typeof message === 'string' &&
        /^(%cJsSIP:|JsSIP:|rtcs:(queue|agent):status:)/.test(message)
      ) {
        return;
      }

      try {
        this.sendLogEvent(level, key, message, args, error);
      } catch {
        // Silently ignore errors (e.g., circular reference in args)
        // These are just captured console.log messages, not critical to send
      }
    });
  }

  public get sender(): LogSender {
    return this._logSender;
  }

  public get log(): Log {
    return this._logService;
  }

  private getUserContextData(): ArbitraryData {
    return this._userId
      ? {
          'User.Id': this._userId,
          'User.AccountId': this._accountId ?? '',
          'User.ContactCenterId': this._contactCenterId ?? '',
          'User.OrgNodeId': this._orgId ?? '',
        }
      : {};
  }

  private sendLogEvent(
    level: string | string[],
    key: string,
    message: any,
    args?: any[],
    error?: unknown,
  ): void {
    const logItem = {
      category: key,
      message: message,
      data: { ConsoleArgs: args as any },
    };

    if (level === 'error') {
      this._logService.error({
        ...logItem,
        error,
      });
    } else if (level === 'warn') {
      this._logService.warning(logItem);
    } else {
      this._logService.info(logItem);
    }
  }
}

export default new KibanaModule(appManager.events, appManager.logger('Kibana'));
